import type { ReactNode } from 'react';
import { CodeText } from './Code';

type Modality = 'deterministic' | 'ml' | 'rag' | 'genai' | 'rpa';
type ModalityClass = 'Deterministic' | 'Self-learning' | 'Deterministic + learning' | 'Generative (gated)';
type LearningProperty = 'Self-learning' | 'Parameter-calibrated' | 'Static';
type HitlLevel = 'L1 sampling' | 'L2 exception review' | 'L3 approval gating' | 'L4 human decision';
type Moat = 'core' | 'commodity';

interface InterventionTabProps {
  // The five-part view derived from the operating-model doc
  objective: string;                  // one line, tied to a strategic objective (e.g., "O2 · migrate to functional/BFY")
  workflow: string[];                 // 3-4 named human sub-steps
  decisionRules: {
    rules: string[];                  // threshold descriptions
    vetoes?: string[];                // non-objective vetoes that hard-block (e.g., ["N1", "N3"])
  };
  aiLogic: {
    description: string;              // 1-2 sentences on the AI logic (trust statement, never the named algorithm — §13.2)
    modality?: Modality;             // legacy; superseded by modalityClass
    modalityClass?: ModalityClass;   // the trust class shown in the tool
    learning?: LearningProperty;     // Self-learning / Parameter-calibrated / Static
    learningState?: { last_retrain?: string | null; trend?: string | null; last_calibration?: string | null; data_window?: string | null } | null;
    hitlLevel: HitlLevel;
    moat?: Moat;                      // Core (protect/automate/invest) vs Commodity (standardize/outsource)
  };
  kpi: {
    primary: string;                  // e.g., "Cycle time concept→shelf"
    downstream?: string;              // what this feeds (e.g., "into Launch-Lab A/B")
    owner: string;                    // who acts on the output
  };
  // The actual interactive content — what the operator clicks/types in
  content: ReactNode;
}

const MODALITY_COLOR: Record<Modality, string> = {
  deterministic: 'var(--olive)',
  ml: 'var(--blue)',
  rag: 'var(--teal)',
  genai: 'var(--blossom)',
  rpa: 'var(--gold)',
};

const MODALITY_LABEL: Record<Modality, string> = {
  deterministic: 'Deterministic',
  ml: 'ML',
  rag: 'RAG',
  genai: 'GenAI',
  rpa: 'RPA',
};

const HITL_COLOR: Record<HitlLevel, string> = {
  'L1 sampling': 'var(--olive)',
  'L2 exception review': 'var(--gold)',
  'L3 approval gating': 'var(--amber)',
  'L4 human decision': 'var(--red)',
};

// V8 §13.2: the tool shows the trust CLASS, never the named algorithm.
const CLASS_COLOR: Record<ModalityClass, string> = {
  'Deterministic': 'var(--olive)',
  'Self-learning': 'var(--blue)',
  'Deterministic + learning': 'var(--teal)',
  'Generative (gated)': 'var(--blossom)',
};
const LEGACY_TO_CLASS: Record<Modality, ModalityClass> = {
  deterministic: 'Deterministic',
  ml: 'Self-learning',
  rag: 'Generative (gated)',
  genai: 'Generative (gated)',
  rpa: 'Deterministic',
};

export default function InterventionTab({ objective, workflow, decisionRules, aiLogic, kpi, content }: InterventionTabProps) {
  return (
    <div>
      {/* Five-part header strip */}
      <div className="panel" style={{ marginBottom: 14, padding: 0, overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 0 }}>
          <Part label="Objective">
            <div style={{ fontSize: 14, fontWeight: 500 }}>{objective.replace(/^O\d+\s*·\s*/, '')}</div>
          </Part>
          <Part label="Workflow">
            <ol style={{ paddingLeft: 18, margin: 0, fontSize: 13, lineHeight: 1.5 }}>
              {workflow.map((step, i) => (
                <li key={i} style={{ marginBottom: 2 }}><CodeText text={step} compact /></li>
              ))}
            </ol>
          </Part>
          <Part label="Decision rules">
            <ul style={{ paddingLeft: 16, margin: 0, fontSize: 13, lineHeight: 1.5, listStyle: 'square' }}>
              {decisionRules.rules.map((r, i) => (
                <li key={i} style={{ marginBottom: 2 }}><CodeText text={r} compact /></li>
              ))}
            </ul>
            {decisionRules.vetoes && decisionRules.vetoes.length > 0 && (
              <div style={{ marginTop: 6 }}>
                <div className="small faint" style={{ marginBottom: 4, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Hard vetoes</div>
                <div className="row" style={{ gap: 4, flexWrap: 'wrap' }}>
                  {decisionRules.vetoes.map((v) => (
                    <span key={v} className="tag red"><CodeText text={v} compact /></span>
                  ))}
                </div>
              </div>
            )}
          </Part>
          <Part label="AI logic">
            <div style={{ fontSize: 13, lineHeight: 1.5, marginBottom: 8 }}>{aiLogic.description}</div>
            <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
              {(() => {
                const cls: ModalityClass = aiLogic.modalityClass ?? (aiLogic.modality ? LEGACY_TO_CLASS[aiLogic.modality] : 'Deterministic');
                return (
                  <span className="tag" title="Trust class — how to read the answer (the method itself is internal)" style={{ background: 'transparent', border: `1px solid ${CLASS_COLOR[cls]}`, color: CLASS_COLOR[cls] }}>
                    {cls}
                  </span>
                );
              })()}
              {aiLogic.learning && (
                <span className="tag" title="Learning property" style={{ background: 'transparent', border: '1px solid var(--muted)', color: 'var(--muted)' }}>
                  {aiLogic.learning === 'Self-learning' ? '↻ Self-learning' : aiLogic.learning === 'Parameter-calibrated' ? '⊹ Parameter-calibrated' : '— Static'}
                </span>
              )}
              <span className="tag" style={{ background: 'transparent', border: `1px solid ${HITL_COLOR[aiLogic.hitlLevel]}`, color: HITL_COLOR[aiLogic.hitlLevel] }}>
                {aiLogic.hitlLevel}
              </span>
              {aiLogic.moat && (
                <span
                  className="tag"
                  title={aiLogic.moat === 'core' ? 'Core · contributes to the 99¢ moat — protect, automate, invest' : 'Commodity · does not build moat — standardize, outsource, minimize'}
                  style={
                    aiLogic.moat === 'core'
                      ? { background: 'var(--amber)', border: '1px solid var(--amber)', color: 'var(--on-amber)', fontWeight: 600 }
                      : { background: 'transparent', border: '1px dashed var(--muted)', color: 'var(--muted)' }
                  }
                >
                  {aiLogic.moat === 'core' ? '◆ Core' : '○ Commodity'}
                </span>
              )}
            </div>
            {aiLogic.learningState && (aiLogic.learningState.last_retrain || aiLogic.learningState.last_calibration) && (
              <div className="small faint" style={{ marginTop: 8, fontSize: 11.5, fontFamily: '"JetBrains Mono", monospace' }}>
                {aiLogic.learningState.last_retrain
                  ? `retrained ${aiLogic.learningState.last_retrain}${aiLogic.learningState.trend ? ` · ${aiLogic.learningState.trend}` : ''}`
                  : `calibrated ${aiLogic.learningState.last_calibration}${aiLogic.learningState.data_window ? ` · ${aiLogic.learningState.data_window}` : ''}`}
              </div>
            )}
          </Part>
          <Part label="KPI / outputs" last>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>{kpi.primary}</div>
            {kpi.downstream && <div className="small" style={{ marginBottom: 4 }}>{kpi.downstream}</div>}
            <div className="small faint">Owner: <b style={{ color: 'var(--ink)' }}>{kpi.owner}</b></div>
          </Part>
        </div>
      </div>

      {/* Operator content (sliders, charts, tables, etc.) */}
      {content}
    </div>
  );
}

function Part({ label, children, last }: { label: string; children: ReactNode; last?: boolean }) {
  return (
    <div style={{ padding: '14px 16px', borderRight: last ? 'none' : '1px solid var(--line-soft)' }}>
      <div className="small faint" style={{ marginBottom: 8, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>
        {label}
      </div>
      {children}
    </div>
  );
}
